/**
 * AlertRegionCard — one card per region currently under alert.
 *
 * Renders a red-accented Chakra Card with the region name, alert types,
 * elapsed duration (ticks in real time), counts of affected
 * locations/organizations, and — when at least one of the underlying events
 * is a simulation — a SIMULATION badge with an auto-end countdown and a
 * "Stop simulation" button.
 *
 * 014-alerts / US7 / T131.
 */
import { useEffect, useState } from 'react';
import { Badge, Box, Button, Flex, HStack, Spacer, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { AdminAlertRegionGroup } from '../api/adminAlertsApi';

interface Props {
  group: AdminAlertRegionGroup;
  onEndSimulation?: (alertEventId: string) => void;
  isEndingSimulation?: boolean;
}

export function AlertRegionCard({ group, onEndSimulation, isEndingSimulation = false }: Props) {
  const { t } = useTranslation();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsedSeconds = Math.max(0, (now - new Date(group.earliestStartedAt).getTime()) / 1000);

  const autoEndsInSeconds =
    group.simulationAutoEndsAt !== null
      ? Math.max(0, (new Date(group.simulationAutoEndsAt).getTime() - now) / 1000)
      : null;

  return (
    <Box
      borderWidth="1px"
      borderColor={group.isSimulated ? 'orange.300' : 'red.300'}
      borderLeftWidth="4px"
      borderLeftColor={group.isSimulated ? 'orange.500' : 'red.500'}
      borderRadius="md"
      p={4}
      bg="white"
      boxShadow="sm"
      data-testid={`alert-region-card-${group.regionId}`}
    >
      <Flex align="center" mb={2}>
        <Stack spacing={1} flex={1}>
          <HStack spacing={2}>
            <Text fontSize="lg" fontWeight="bold" color="red.700">
              {group.regionName ?? group.regionId}
            </Text>
            {group.isSimulated && (
              <Badge
                colorScheme="orange"
                textTransform="uppercase"
                data-testid={`alert-region-card-${group.regionId}-simulation-badge`}
              >
                {t('admin.alerts.overview.simulationBadge', 'Simulation')}
              </Badge>
            )}
          </HStack>
          <Text fontSize="sm" color="gray.600">
            {group.alertTypes.join(', ')}
          </Text>
        </Stack>
        <Spacer />
        <Stack spacing={0} align="end">
          <Text fontSize="xs" color="gray.500">
            {t('admin.alerts.overview.elapsed', 'Elapsed')}
          </Text>
          <Text fontSize="md" fontWeight="semibold">
            {formatDuration(elapsedSeconds)}
          </Text>
        </Stack>
      </Flex>

      <HStack spacing={6} fontSize="sm" color="gray.700" mt={2}>
        <Text>
          {t('admin.alerts.overview.locations', 'Locations')}: <b>{group.affectedLocationCount}</b>
        </Text>
        <Text>
          {t('admin.alerts.overview.organizations', 'Organizations')}: <b>{group.affectedOrganizationCount}</b>
        </Text>
      </HStack>

      {group.isSimulated && autoEndsInSeconds !== null && (
        <Flex align="center" mt={3}>
          <Text fontSize="sm" color="orange.700">
            {t('admin.alerts.overview.autoEndsIn', 'Auto-ends in')}{' '}
            <b>{formatDuration(autoEndsInSeconds)}</b>
          </Text>
          <Spacer />
          {group.simulationAlertEventId && onEndSimulation && (
            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              isLoading={isEndingSimulation}
              onClick={() => onEndSimulation(group.simulationAlertEventId!)}
              data-testid={`alert-region-card-${group.regionId}-stop-simulation`}
            >
              {t('admin.alerts.overview.stopSimulation', 'Stop simulation')}
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
}

function formatDuration(totalSeconds: number): string {
  const s = Math.floor(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}h ${m.toString().padStart(2, '0')}m`;
  }
  return `${m}m ${sec.toString().padStart(2, '0')}s`;
}
